import Button from '@doordash/component-button'
import { Select } from '@doordash/component-fields'
import { Text, Inset, Spacing, StackChildren } from '@doordash/design-language'
import { flatMap, noop, uniq } from 'lodash'

import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import * as events from '../../../events'
import BackButton from '../../atoms/back-button'
import OrientationTile from '../../molecules/orientation-tile'
import Page from '../../molecules/page'
import PageHeader from '../../molecules/page-header'
import Section from '../../molecules/section'
import NoOrientation from './noOrientation'
import ScheduleOrientationFormHeader from './scheduleOrientationFormHeader'
import { OrientationContainer } from './styles'

import t from './strings'

type Orientation = {
  address: string
  id: number
  startTime?: string
  endTime?: string
  startingPoint: string
}

type ScheduleOrientationFormProps = {
  application: object
  uniqueLink: string
  experiments?: object
  onBackButtonClick?: any
  bookOrientation?: (
    uniqueLink,
    orientationId,
    application,
    experiments
  ) => Promise<any>
  orientations?: Orientation[]
  pickOrientationStartingPoint?: (any) => void
  submitting?: boolean
  selectedStartingPoint?: any
  timezone?: string
  orientationStartingPoint: any
  trackScheduleOrientationFormRendered?: () => Promise<any>
  trackOrientationBooking?: (orientation) => Promise<any>
  submittingOrientation?: () => Promise<any>
  clearingOrientation?: () => Promise<any>
  loadOrientations?: (string) => Promise<any>
}

type ScheduleOrientationFormState = {
  chosenOrientation: any
}
export class ScheduleOrientationForm extends React.Component<
  ScheduleOrientationFormProps,
  ScheduleOrientationFormState
> {
  public static defaultProps = {
    pickOrientationStartingPoint: noop,
    bookOrientation: noop,
    orientations: [],
    trackScheduleOrientationFormRendered: noop,
  }

  public componentDidMount() {
    const { uniqueLink } = this.props
    this.props.trackScheduleOrientationFormRendered()
    if (this.props.loadOrientations) {
      this.props.loadOrientations(uniqueLink)
    }
  }

  public renderPickOrientationsSection(
    startingPoint: any,
    address: string,
    orientations: Orientation[]
  ) {
    const { timezone } = this.props

    const renderedOrientations = orientations.map(orientation => {
      let isSelected = false

      if (this.state && this.state.chosenOrientation.id === orientation.id) {
        isSelected = true
      }

      const onClick = () => {
        this.setState({ chosenOrientation: orientation })
      }
      return (
        <OrientationTile
          onClick={onClick}
          isSelected={isSelected}
          date={orientation.startTime}
          timezone={timezone}
          style={{ marginBottom: `${Spacing.Large}px` }}
        />
      )
    })

    return (
      <Section>
        <PageHeader>
          <Text styles={Text.Styles.Bold24} color={Text.Colors.TextAction}>
            {`${t.availableOrientationsAt()} ${startingPoint}`}
          </Text>
          <Text styles={Text.Styles.PageSubtext}>
            {t.orientationHeldAt()} <strong>{address}</strong>.{' '}
            {t.orientationHeldAtDetails()}
            <Text
              color={Text.Colors.TextAction}
              tag={'a'}
              href="https://www.doordash.com/help/"
            >
              https://www.doordash.com/help/
            </Text>
            .
          </Text>
        </PageHeader>
        <OrientationContainer>{renderedOrientations}</OrientationContainer>
      </Section>
    )
  }

  public handleBookOrientation = () => {
    const { uniqueLink, application, experiments } = this.props
    if (!this.state || !this.state.chosenOrientation.id) {
      return
    }

    const trackingData = {
      timezone: this.props.timezone,
      ...this.state.chosenOrientation,
    }

    this.props.trackOrientationBooking(trackingData)

    this.props.bookOrientation(
      uniqueLink,
      this.state.chosenOrientation.id,
      application,
      experiments
    )
  }

  public render() {
    const {
      pickOrientationStartingPoint,
      orientations,
      submitting,
      onBackButtonClick,
      orientationStartingPoint,
    } = this.props

    if (orientations.length === 0) {
      return <NoOrientation onBackButtonClick={onBackButtonClick} />
    }

    const startingPoints = uniq(flatMap(orientations, 'startingPoint')).map(
      sp => ({
        name: sp,
        value: sp,
      })
    )

    const spOrientations = orientations.filter(
      o => o.startingPoint === orientationStartingPoint
    )
    let renderedPickOrientationsSection
    if (orientationStartingPoint) {
      renderedPickOrientationsSection = this.renderPickOrientationsSection(
        orientationStartingPoint,
        spOrientations[0] ? spOrientations[0].address : '',
        spOrientations
      )
    }

    return (
      <div>
        <Page>
          {onBackButtonClick && <BackButton onClick={onBackButtonClick} />}
          <StackChildren>
            <ScheduleOrientationFormHeader />
            {onBackButtonClick && <BackButton onClick={onBackButtonClick} />}
            <Inset size={Inset.Sizes.XxSmall} isSquished={true}>
              <Select
                label={t.orientationLocation()}
                value={
                  orientationStartingPoint == null
                    ? ''
                    : orientationStartingPoint
                }
                disabledValue={t.orientationLocationDisabled()}
                onChange={value => pickOrientationStartingPoint(value)}
                options={startingPoints}
                size={Select.Sizes.Large}
              />{' '}
            </Inset>
          </StackChildren>
        </Page>
        {renderedPickOrientationsSection}
        <Button onClick={this.handleBookOrientation} loading={submitting}>
          {t.submitButton()}
        </Button>
      </div>
    )
  }
}

const mapStateToProps = state => {
  return {
    application: state.application,
    submittingOrientation: state.submittingOrientation,
    clearingOrientation: state.clearingOrientation,
    orientations: state.orientations,
    experiments: state.experiments,
    orientationStartingPoint: state.orientationStartingPoint,
  }
}

const mapDispatchToProps = {
  trackScheduleOrientationFormRendered:
    events.trackScheduleOrientationFormRendered,
  trackOrientationBooking: events.trackOrientationBooking,
  pickOrientationStartingPoint: actions.pickOrientationStartingPoint,
  bookOrientation: actions.bookOrientation,
  loadOrientations: actions.loadOrientations,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ScheduleOrientationForm)
